import { ParentTopicNode, AgendaItemsFormValues, AgendaProject } from '../agendaTypes'

const TH_MONTHS = [
    'มกราคม',
    'กุมภาพันธ์',
    'มีนาคม',
    'เมษายน',
    'พฤษภาคม',
    'มิถุนายน',
    'กรกฎาคม',
    'สิงหาคม',
    'กันยายน',
    'ตุลาคม',
    'พฤศจิกายน',
    'ธันวาคม',
]

const fmtTh = (iso?: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return `${d.getDate()} ${TH_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`
}

export function buildProjectDescription(it: any): string {
    const nameTh = it?.projectName_th ?? '-'
    const nameEn = it?.projectName_en ?? '-'
    const researcher = it?.researcherName_th ?? it?.researcherName_en ?? '-'
    const faculty = it?.faculty_name ?? '-'
    const committee = it?.committee ?? '-'
    const layperson = it?.layperson ?? '-'
    const typeTh = it?.consider_remark || '-'
    const approvedDate = it?.export_certificate_date || '-'

    return [
        `ชื่อโครงการ (ภาษาไทย) : ${nameTh ?? '-'}`,
        `ชื่อโครงการ (ภาษาอังกฤษ) : ${nameEn ?? '-'}`,
        `ผู้วิจัยหลัก : ${researcher ?? '-'}`,
        `หน่วยงาน : ${faculty ?? '-'}`,
        `กรรมการผู้ทบทวนหลัก : ${committee ?? '-'}`,
        `Layperson : ${layperson ?? '-'}`,
        `ลักษณะโครงการ : ${typeTh ?? '-'}`,
        `วันที่รับรอง : ${approvedDate ?? '-'}`,
    ].join('\n')
}

export type ReviewSummaryRow = {
    projectID: number
    submittedID: number
    approveStatusID?: number | null
    approveStatusName_th?: string | null
    approveStatusName_en?: string | null
    section: 'STATUS' | 'TOPIC'
    topicNo?: number | null
    topicName_th?: string | null
    topicName_en?: string | null
    comment?: string | null
}

/** อนุมัติ แบบมีข้อคิดเห็น  REC1 = 8, REC2 = 21
 *  หน้าจอแสดงข้อความ LABEL.APPROVE แทนชื่อจริงของสถานะ (resultOfConsider.tsx:118-120) */
const APPROVE_WITH_COMMENT = [8, 21]
/** อนุมัติ (1,17) และ อนุมัติให้ดำเนินการวิจัยต่อเนื่อง (9,22)
 *  หน้าจอไม่แสดงหัวข้อ แสดงแค่บรรทัดผล (resultOfConsider.tsx:48-55 isShowComment) */
const HIDE_TOPIC_STATUS = [1, 17, 9, 22]

export function buildReviewSummary(rows: ReviewSummaryRow[], lang: string): string {
    if (!rows || rows.length === 0) return ''
    const isTh = lang !== 'en'
    const out: string[] = []

    const status = rows.find((r) => r.section === 'STATUS')
    const statusId = status?.approveStatusID ?? null

    if (statusId != null) {
        const statusName = APPROVE_WITH_COMMENT.includes(statusId)
            ? isTh
                ? 'อนุมัติ (แต่ยังจำเป็นต้องมีการแก้ไข)'
                : 'Revisions required'
            : (isTh ? status?.approveStatusName_th : status?.approveStatusName_en) || ''
        if (statusName) {
            out.push(`${isTh ? 'ผลการพิจารณา' : 'Ethic review result'} : ${statusName}`)
            out.push('')
        }
    }

    // ผลเป็นอนุมัติ หน้าจอไม่แสดงหัวข้อ
    if (statusId != null && HIDE_TOPIC_STATUS.includes(statusId)) {
        return out.join('\n').trim()
    }

    rows.forEach((r) => {
        if (r.section !== 'TOPIC') return
        const text = (r.comment ?? '').trim()
        if (!text) return

        const topicName = (isTh ? r.topicName_th : r.topicName_en) || ''
        if (r.topicNo != null && topicName) {
            out.push(`${r.topicNo}. ${topicName}`)
        } else {
            out.push(isTh ? 'ข้อคิดเห็น / ข้อสังเกต' : 'Suggestion / Notice')
        }
        out.push(text)
        out.push('')
    })

    return out.join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

export const mapProjectToForm = (item: any): AgendaProject => {
    const projectId = item?.projectID ?? item?.projectId ?? null
    return {
        projectId: projectId != null ? Number(projectId) : null,
        projectNumber: item?.projectNumber ?? item?.projectCode ?? null,
        projectDescription: buildProjectDescription(item),
        // ProjectPicker แนบมาให้ตอนกดยืนยัน ถ้าไม่มีปล่อยว่างให้เจ้าหน้าที่พิมพ์เอง
        projectSummary: item?.reviewSummaryText ?? '',
    }
}

// กรองเอาแค่รอบเดียวไม่เอาซ้ำ
export function pushProjectsUnique(fields: any, items: any[]): void {
    const exists = new Set<number>(
        ((fields?.value ?? []) as AgendaProject[])
            .map((p) => Number(p?.projectId))
            .filter((n) => Number.isFinite(n)),
    )

    items.forEach((it) => {
        const mapped = mapProjectToForm(it)
        const id = Number(mapped.projectId)

        if (Number.isFinite(id)) {
            if (exists.has(id)) return
            exists.add(id)
        }

        fields.push(mapped)
    })
}

export function serializeAgendaToTopics(form: AgendaItemsFormValues): ParentTopicNode[] {
    const out: ParentTopicNode[] = []

    const ensureProjects = (ps?: AgendaProject[]) =>
        ps && ps.length > 0 ? ps : [{ projectId: null, projectDescription: '' }]

    for (const ag of form.agendas ?? []) {
        const topProjects = ensureProjects(ag.projects)

        const parent: ParentTopicNode = {
            topicId: ag.topicId ?? null,
            parentTopicId: null,
            topicName: ag.title ?? '',
            topicDescription: ag.detail ?? '',
            topicSummary: ag.topicSummary,
            projects: topProjects,
            children: [],
        }

        for (const sub of ag.subs ?? []) {
            const subProjects = ensureProjects(sub.projects)
            parent.children.push({
                topicId: sub.topicId ?? null,
                parentTopicId: parent.topicId ?? null,
                topicName: sub.title ?? '',
                topicDescription: '',
                topicSummary: sub.topicSummary,
                projects: subProjects,
                children: [],
            })
        }

        out.push(parent)
    }

    return out
}
